import { useEffect, useRef } from 'react';
import { Box, Container, Typography } from '@mui/material';

import type { TextBlockContent } from '@/types/database';

interface InlineTextBlockProps {
  content: TextBlockContent;
  canManage: boolean;
  onCommit: (content: TextBlockContent) => void;
}

// Mirrors TextBlockView's exact markup so a text block looks identical
// whether it's being edited here, viewed in /studio/preview, or published —
// the only difference is contentEditable. Typed text updates the DOM
// directly (uncontrolled, via ref) rather than through React state, so
// there's no re-render-driven cursor jump while typing; the actual write
// is debounced and flushed on blur, same pattern as the drawer's edit
// panels but without a round trip gating what's visibly displayed.
export function InlineTextBlock({ content, canManage, onCommit }: InlineTextBlockProps) {
  const ref = useRef<HTMLElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef(content);

  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const commit = (immediate: boolean) => {
    const text = ref.current?.textContent ?? '';
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (immediate) {
      onCommit({ ...contentRef.current, content: text });
    } else {
      timeoutRef.current = setTimeout(() => onCommit({ ...contentRef.current, content: text }), 500);
    }
  };

  return (
    <Box sx={{ py: 6 }}>
      <Container maxWidth="md">
        <Typography
          ref={ref}
          variant={content.variant ?? 'body1'}
          align={content.align ?? 'left'}
          contentEditable={canManage}
          suppressContentEditableWarning
          onInput={() => commit(false)}
          onBlur={() => commit(true)}
          onClick={(event) => {
            if (canManage) event.stopPropagation();
          }}
          sx={{
            lineHeight: 1.8,
            outline: 'none',
            borderRadius: 0.5,
            cursor: canManage ? 'text' : 'inherit',
            '&:focus': canManage ? { bgcolor: 'action.hover', boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}` } : undefined,
          }}
        >
          {content.content}
        </Typography>
      </Container>
    </Box>
  );
}
